<template>
  <div>
    <div class="history-header-container">
      <div class="history-content-container">
        <div v-if="!isNoteUploadInfo">
          <HistoryComponentHeader
            @send-note-and-uploadfile="sendNoteAndFile($event)"
            @note-data-deleted="resetNoteComponent = false"
            :resetNoteComponent="resetNoteComponent"
            :withContainerStyle="false"
            :showInternCheckbox="true"
          >
            <template v-slot:status-alert>
              <v-alert
                class="alert_success"
                style="background-color: #e4f0d6 !important"
                v-if="alert"
                max-width="270px"
                max-height="35px"
                text
                transition="scale-transition"
              >
                <template v-slot:append>
                  <v-icon color="green" class="ml-2">mdi-check</v-icon>
                </template>
                Erfolgreich gespeichert
              </v-alert>
              <v-alert
                type="error"
                class="alert_error mt-4"
                max-width="270px"
                v-if="alertError"
                text
                transition="scale-transition"
              >
                Speicherung fehlgeschlagen
              </v-alert>
            </template>
          </HistoryComponentHeader>
        </div>
        <div v-else class="history-upload-state">
          <div class="container--note-upload-pending">
            <v-progress-circular
              indeterminate
              color="primary"
              :size="60"
              :width="5"
            />
            <NewAlert
              class="mt-4"
              color="INFO"
              :message="$t('emobility_inspection.info_file_upload')"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="list-group-wrapper">
      <transition name="fade">
        <div class="loading" v-show="loading">
          <v-progress-circular
            indeterminate
            :size="75"
            color="#9B004B"
          ></v-progress-circular>
        </div>
      </transition>
      <div class="list-group" id="infinite-list">
        <HistoryComponentScroll
          :historyType="historyType"
          :showClerc="true"
          v-for="(historyItem, index) in historyData"
          :key="index"
          :historyItem="historyItem"
          @update-show-details="historyItem.show_details = $event"
        >
        </HistoryComponentScroll>
      </div>
    </div>
  </div>
</template>

<script>
import { HistoryComponentScroll } from '@newnetzportal/netzportal-webapp-storybook-components';
import orderService from '../../services/OrderService';
import { uploadFile } from '@/services/FileUploadService';
import noteService from '../../services/NoteService';
import DocumentsService from '../../services/DocumentsService';
import {
  transformRawDataToHistoryTableFormat,
  mapEventNameToTitle,
} from './transform_raw_data_to_history_table_format';
import { EventBus } from '@/main';
import { emptyEventData } from '@/store/ordersModule';
import {
  HistoryComponentHeader,
  NewAlert,
} from '@newnetzportal/netzportal-webapp-storybook-components';
import { mixinData } from '@/helpers/handleJSONPathKeys';
import { getS3BucketName } from '../../helpers/S3BucketNames';
import {
  isEventElectricityJourney,
  normalizeEventElectricityHistory,
} from './event_electricity_history';
import { mergeUniqueHistoryChanges } from './mergeUniqueHistoryChanges';

const HIDDEN_HISTORY_FIELDS = [
  'updateInstallerBadgeId',
  'updateOrderCaseNumber',
  'updateEmobilityCaseNumber',
  'updatePhotovoltaicCaseNumber',
  'addOrderFileHistoryEntry',
  'addCommissioningFileHistoryEntry',
  'addEmobilityFileHistoryEntry',
  'addInstallerFileHistoryEntry',
  'addPhotovoltaicFileHistoryEntry',
  'addConsumptionDeviceFileHistoryEntry',
  'updateCommissioningOrderLastStatusChange',
];

export default {
  name: 'HistoryTable',
  components: {
    HistoryComponentScroll,
    HistoryComponentHeader,
    NewAlert,
  },
  props: {
    historyType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      allChanges: [],
      selectedOrder: this.$store.getters.getSelectedOrder,
      selectedCommissioning: this.$store.getters.getSelectedCommissioning,
      selectedInstaller: this.$store.getters.getSelectedInstaller,
      selectedEmobility: this.$store.getters.getSelectedEmobility,
      selectedPhotovoltaic: this.$store.getters.getSelectedPhotovoltaic,

      loading: false,
      all_data_loaded: false,
      alert: false,
      alertError: false,
      resetNoteComponent: false,
      order_id: this.$route.params.id ? this.$route.params.id : '',
      isNoteUploadInfo: false,
    };
  },
  created() {
    EventBus.$on(
      'update-history-table-' + this.historyType.toLowerCase(),
      (event) => {
        this.allChanges = mergeUniqueHistoryChanges(this.allChanges, [event]);
        if (this.allChanges.length > 0) {
          const sortedActivities = this.allChanges.sort(
            (a, b) => new Date(b.created) - new Date(a.created)
          );
          this.historyData = sortedActivities
            .filter((item) => !HIDDEN_HISTORY_FIELDS.includes(item.field))
            .flatMap((item) => {
              try {
                const mapped = this.tableHistoryMapper(item);
                return Array.isArray(mapped) ? mapped : [mapped];
              } catch (error) {
                console.error(
                  'Error in tableHistoryMapper:',
                  error,
                  'Item:',
                  item
                );
                return []; // Safely handle any errors by skipping this item
              }
            });
        }
      }
    );
    this.getHistoryFromBackend(true);
    this.$store.commit('setLastUpdatedComponent', '');
  },
  mounted() {
    // Detect when scrolled to bottom.
    const listElm = document.querySelector('#infinite-list');
    listElm.addEventListener('scroll', () => {
      if (
        listElm.scrollTop + listElm.clientHeight >= listElm.scrollHeight &&
        !this.all_data_loaded
      ) {
        this.getHistoryFromBackend();
      }
    });
  },
  watch: {
    notifyEvent(newValue) {
      if (
        this.lastUpdatedComponent === 'historyTable' &&
        (newValue.field === 'addOrderNote' ||
          newValue.field === 'addCommissioningNote' ||
          newValue.field === 'addInstallerNote' ||
          newValue.field === 'addEmobilityNote')
      ) {
        this.alert = true;
        setTimeout(() => {
          (this.alert = false),
            this.$store.commit('setNotifyEvent', emptyEventData);
        }, 3000);
      }
    },
  },
  computed: {
    historyData: {
      get() {
        return this.$store.getters['get' + this.historyType + 'HistoryData'];
      },
      set(value) {
        this.$store.commit(`set${this.historyType}HistoryData`, value);
      },
    },
    notifyEvent() {
      return this.$store.getters.getNotifyEvent
        ? this.$store.getters.getNotifyEvent
        : emptyEventData;
    },
    lastUpdatedComponent() {
      return this.$store.getters.getLastUpdatedComponent;
    },
    user_id() {
      return this.$store.getters.getUserId;
    },
    selectedConsumptionDevice() {
      return this.$store.getters.getSelectedConsumptionDevice;
    },
  },
  methods: {
    getHistoryFromBackend(first_load) {
      this.loading = true;
      if (first_load) {
        this.allChanges = [];
      }
      const params = {
        id: this.order_id,
        from_index: this.allChanges.length,
        limit: 20,
      };
      orderService['get' + this.historyType + 'History'](params)
        .then((res) => {
          const lowerFirst = (string) =>
            string.charAt(0).toLowerCase() + string.slice(1);
          const responseChanges =
            res[lowerFirst(this.historyType) + 'History'].changes;
          this.allChanges = mergeUniqueHistoryChanges(
            this.allChanges,
            responseChanges || []
          );
          this.historyData = this.allChanges
            .filter((item) => !HIDDEN_HISTORY_FIELDS.includes(item.field))
            .flatMap((change) => {
              const mapped = this.tableHistoryMapper(change);
              return Array.isArray(mapped) ? mapped : [mapped]; // Flatten arrays of objects
            });
          if (
            res[lowerFirst(this.historyType) + 'History'].count_changes ===
            this.allChanges.length
          ) {
            this.all_data_loaded = true;
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    tableHistoryMapper(change) {
      const payload = JSON.parse(change.payload);

      //  prone to delay fields mapped to event names
      const reminderFields = {
        resendInstallerECReminder: 'extend_any_expiry_date_last_reminder',
        resendPVICEIReminder:
          'installation_company_electricity_id_last_reminder',
        resendPVIPReminder: 'implementation_protocol_pending_reminder',
        resendPVIReminder: 'implementation_pending_reminder',
        resendPVFDReminder: 'finance_data_last_reminder',
        resendPVMStRDReminder: 'maStR_data_last_reminder',
        resendPVStorageMStRDReminder: 'storage_maStR_data_last_reminder',
        resendCDICMReminder: 'installation_company_missing_last_reminder',
        resendCDTechnicalDataReminder: 'technical_data_last_reminder',
      };

      if ('delay' in payload && payload.delay > 0) {
        const reminderKey = reminderFields[change.field];
        if (reminderKey && payload.new_value?.[reminderKey]) {
          const oldDateString = payload.new_value[reminderKey];
          const reminderDate = new Date(oldDateString);
          reminderDate.setSeconds(reminderDate.getSeconds() + payload.delay);
          payload.new_value[reminderKey] = reminderDate.toISOString();
        }
      }

      const productName =
        this.historyType === 'Order'
          ? this.selectedOrder.product
          : this.historyType === 'Commissioning'
          ? this.selectedCommissioning.product ||
            this.selectedCommissioning.products
          : this.selectedInstaller.concession_division;
      // Status-related mappings
      if (
        change.field === 'notifyOrderStatus' ||
        ((change.field === 'updateOrderStatus' ||
          change.field === 'updateCommissioningOrderStatus' ||
          change.field === 'updatePVStatus' ||
          change.field === 'updateCDStatus' ||
          change.field === 'updateInstallerConcessionStatus' ||
          change.field === 'updateEmobilityStatus') &&
          payload.old_value.status)
      ) {
        return {
          date: change.created,
          clerk: change.changed_by,
          old_status: payload.old_value.status,
          new_status: payload.new_value.status,
          show_details: false,
          field: change.field,
        };
      }

      // Other mappings
      if (change.field === 'updateOrderSAPKeys') {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          new_value: null,
          old_value: transformRawDataToHistoryTableFormat(
            payload.new_value,
            this.selectedOrder.product
          ),
          show_details: false,
          field: change.field,
        };
      }

      if (
        (change.field === 'addEmobilityNote' ||
          change.field === 'addOrderNote' ||
          change.field === 'addCommissioningNote' ||
          change.field === 'addInstallerNote' ||
          change.field === 'addPhotovoltaicNote' ||
          change.field === 'addConsumptionDeviceNote') &&
        payload.filename
      ) {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          note: payload.note,
          new_value: null,
          old_value: transformRawDataToHistoryTableFormat({
            note_file: payload.filename,
          }),
          show_details: false,
          field: change.field,
        };
      }

      if (
        change.field === 'addEmobilityNote' ||
        change.field === 'addOrderNote' ||
        change.field === 'addCommissioningNote' ||
        change.field === 'addInstallerNote' ||
        change.field === 'addPhotovoltaicNote' ||
        change.field === 'addConsumptionDeviceNote' ||
        change.field === 'createConsumptionDeviceInspectionRequest'
      ) {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          note: payload.note,
          new_value: null,
          old_value: null,
          show_details: false,
          field: change.field,
        };
      }

      if (
        change.field === 'updateOrderSAPError' ||
        change.field === 'notifyOrderSAPError'
      ) {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          new_value: transformRawDataToHistoryTableFormat(
            payload.new_value,
            this.selectedOrder.product
          ),
          old_value: payload.old_value.error,
          show_details: false,
          field: change.field,
        };
      }

      if (change.field === 'updateInstallerFilesName') {
        const result = this.processPayload(payload);
        const historyObject = this.mapHistoryObject(
          change,
          productName,
          result
        );
        return historyObject;
      }

      if (change.field === 'renewInstallerConcession') {
        const productName =
          this.historyType === 'Order'
            ? this.selectedOrder.product
            : this.selectedInstaller.concession_division;

        const statusObject = this.mapStatusObject(change, payload);
        const result = this.processPayload(payload);
        const historyObject = this.mapHistoryObject(
          change,
          productName,
          result
        );
        return [statusObject, historyObject];
      }

      if (
        change.field === 'updateTechnicalInspectionData' ||
        change.field === 'updateInstallerInspectionData' ||
        change.field === 'updateEmobilityInspectionData' ||
        (change.field === 'updateCommissioningTechnicalInspectionData' &&
          payload.status === 'completed') ||
        (change.field === 'updateConsumptionDeviceTechnicalInspectionData' &&
          payload.status === 'completed')
      ) {
        change.status = payload.status;
        return this.mapStatusObject(change, payload);
      }

      if (
        change.field === 'createEMobilityRequest' &&
        payload.derived_from_order
      ) {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          old_value: transformRawDataToHistoryTableFormat(
            payload,
            this.selectedOrder.product
          ),
          new_value: null,
          show_details: false,
          field: change.field,
        };
      }

      if (change.field === 'updateInstallerConcessionExpiry') {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          old_value: payload.old_value
            ? transformRawDataToHistoryTableFormat(
                payload.old_value,
                this.selectedOrder.product
              )
            : ' ',
          new_value: payload.new_value
            ? transformRawDataToHistoryTableFormat(
                payload.new_value,
                this.selectedOrder.product
              )
            : ' ',
          show_details: false,
          field: change.field,
        };
      }

      if (
        change.field === 'resetOrderEmobilityPayload' ||
        change.field === 'createPVImplementationProtocolRequest' ||
        change.field === 'createPVImplementationRequest' ||
        change.field === 'createPVRequest' ||
        change.field === 'deliverPVConfirmation'
      ) {
        return {
          change_title: mapEventNameToTitle(change, payload.title),
          date: change.created,
          clerk: change.changed_by,
          old_value: null,
          new_value: payload.new_value,
          show_details: false,
          field: change.field,
        };
      }

      if (reminderFields[change.field]) {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          new_value: transformRawDataToHistoryTableFormat(
            payload.new_value,
            this.selectedOrder.product,
            change.field
          ),
          old_value: ' ',
          show_details: false,
          field: change.field,
        };
      }

      if (change.field === 'resendFile') {
        return {
          date: change.created,
          change_title: mapEventNameToTitle(change, payload.title),
          clerk: change.changed_by,
          new_value: transformRawDataToHistoryTableFormat(
            payload,
            this.selectedOrder.product,
            change.field
          ),
          old_value: ' ',
          show_details: false,
          field: change.field,
        };
      }

      // updateCommissioningOrderRequest ships old/new as delta.prev/delta.target instead of
      // old_value/new_value (mirrors the fallback added to fn_notify_events_dev in netzportal-infra-backoffice)
      if (change.field === 'updateCommissioningOrderRequest' && payload.delta) {
        payload.old_value = payload.old_value ?? payload.delta.prev;
        payload.new_value = payload.new_value ?? payload.delta.target;
      }

      payload.old_value = mixinData(payload.old_value);
      payload.new_value = mixinData(payload.new_value);

      const journeyType = (
        this.selectedCommissioning.journey_type ||
        this.selectedCommissioning.payload?.journey_type ||
        ''
      )
        .trim()
        .toUpperCase();
      const isCommissioningUpdate =
        change.field === 'updateCommissioningOrderRequest' ||
        change.field === 'updateCommissioningOrderData';
      const isEventElectricityHistory =
        this.historyType === 'Commissioning' &&
        isEventElectricityJourney(journeyType);

      if (isEventElectricityHistory) {
        normalizeEventElectricityHistory(payload, {
          isCommissioningUpdate,
          removeEmptyCommissioningChanges:
            this.removeEmptyCommissioningChanges.bind(this),
          removeIdenticalValues: this.removeIdenticalValues.bind(this),
        });
      } else if (
        isCommissioningUpdate &&
        payload.old_value &&
        payload.new_value
      ) {
        this.removeEmptyCommissioningChanges(
          payload.old_value,
          payload.new_value
        );
        this.removeIdenticalValues(payload.old_value, payload.new_value);
      }

      const object = this.mapHistoryObject(change, productName, payload);
      return object;
    },
    processPayload(payload) {
      const { old_value, new_value } = payload;
      if (old_value.responsible_specialists) {
        old_value.responsible_specialists.forEach((specialist) => {
          delete specialist.energy_types_de;
        });
      }
      this.removeIdenticalValues(old_value, new_value);
      return { ...payload, old_value, new_value };
    },
    removeIdenticalValues(oldObj, newObj) {
      const isObject = (val) =>
        val && typeof val === 'object' && !Array.isArray(val);

      const areValuesEmpty = (val1, val2) =>
        (val1?.toString() ?? '') === '' && (val2?.toString() ?? '') === '';

      const areArraysEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) return false;
        const unmatchedItems = [];
        for (let i = 0; i < arr1.length; i++) {
          const item1 = arr1[i];
          const item2 = arr2[i];

          if (isObject(item1) && isObject(item2)) {
            const oldItem = { ...item1 };
            const newItem = { ...item2 };
            this.removeIdenticalValues(oldItem, newItem);
            if (
              Object.keys(oldItem).length === 0 &&
              Object.keys(newItem).length === 0
            ) {
              continue;
            }
          } else if (item1 === item2) {
            continue;
          }
          unmatchedItems.push(item1);
        }

        return unmatchedItems.length === 0;
      };

      for (const key in oldObj) {
        if (key in newObj) {
          if (isObject(oldObj[key]) && isObject(newObj[key])) {
            // Recursively compare nested objects
            this.removeIdenticalValues(oldObj[key], newObj[key]);

            if (Object.keys(oldObj[key]).length === 0) delete oldObj[key];
            if (Object.keys(newObj[key]).length === 0) delete newObj[key];
          } else if (Array.isArray(oldObj[key]) && Array.isArray(newObj[key])) {
            // Compare arrays, including arrays of objects
            if (areArraysEqual(oldObj[key], newObj[key])) {
              delete oldObj[key];
              delete newObj[key];
            }
          } else if (
            areValuesEmpty(oldObj[key], newObj[key]) ||
            oldObj[key] === newObj[key]
          ) {
            // Remove if both values are empty or identical
            delete oldObj[key];
            delete newObj[key];
          }
        }
      }
    },
    removeEmptyCommissioningChanges(oldObj, newObj) {
      const isEmpty = (value) =>
        value === null || value === undefined || value === '';

      if (Array.isArray(oldObj) && Array.isArray(newObj)) {
        const length = Math.min(oldObj.length, newObj.length);
        for (let index = 0; index < length; index++) {
          this.removeEmptyCommissioningChanges(oldObj[index], newObj[index]);
        }
        return;
      }

      const isObject = (value) =>
        value && typeof value === 'object' && !Array.isArray(value);

      if (!isObject(oldObj) || !isObject(newObj)) {
        return;
      }

      Object.keys(newObj).forEach((key) => {
        if (!(key in oldObj) && isEmpty(newObj[key])) {
          delete newObj[key];
        } else if (key in oldObj) {
          this.removeEmptyCommissioningChanges(oldObj[key], newObj[key]);
        }
      });

      Object.keys(oldObj).forEach((key) => {
        if (!(key in newObj) && isEmpty(oldObj[key])) {
          delete oldObj[key];
        }
      });
    },
    mapStatusObject(change, payload) {
      return {
        date: change.created,
        change_title: mapEventNameToTitle(change, payload.title),
        clerk: change.changed_by,
        status: payload.status,
        old_status: payload.old_value?.status,
        new_status: payload.new_value?.status,
        show_details: false,
        field: change.field,
      };
    },
    mapHistoryObject(change, productName, payload) {
      const journeyType =
        this.historyType === 'Commissioning'
          ? (
              this.selectedCommissioning.journey_type ||
              this.selectedCommissioning.payload?.journey_type ||
              ''
            )
              .trim()
              .toUpperCase()
          : undefined;

      return {
        date: change.created,
        change_title: mapEventNameToTitle(change, payload.title),
        note: payload.note
          ? payload.note
          : payload.remark
          ? payload.remark
          : null,
        clerk: change.changed_by,
        old_value: payload.old_value
          ? transformRawDataToHistoryTableFormat(
              payload.old_value,
              productName ? productName : '',
              change.field,
              journeyType
            )
          : null,
        new_value: payload.new_value
          ? transformRawDataToHistoryTableFormat(
              payload.new_value,
              productName ? productName : '',
              change.field,
              journeyType
            )
          : null,
        show_details: false,
        field: change.field,
      };
    },
    async sendNoteAndFile(value) {
      this.isNoteUploadInfo = true;
      this.alertError = false;
      const noteObj = {
        id: this.order_id,
        note: value.note,
        title: value.title,
        intern: value.intern ?? false,
        filename: value.filename,
        user_id: this['selected' + this.historyType].user_id,
      };
      this.$store.commit('setLastUpdatedComponent', 'historyTable');
      if (value.note) {
        try {
          const userResponse = await noteService[
            'add' + this.historyType + 'Note'
          ](noteObj, this.user_id);

          // Has to get the "_persistent_event_id_" from Response; used for s3-subfoldering
          if (value.filename) {
            const bucketName = getS3BucketName(this.historyType);
            const url = (
              await DocumentsService.putDocumentToS3(
                `${this.order_id}/${
                  userResponse['add' + this.historyType + 'Note']
                    ._persistent_event_id_
                }/${value.filename}`,
                bucketName
              )
            ).uploadFile;
            await uploadFile(url, value.file);
            this.resetNoteComponent = true;
          }
          this.resetNoteComponent = true;
          this.isNoteUploadInfo = false;
        } catch (err) {
          console.error(err);
          this.isNoteUploadInfo = false;
          this.alertError = true;
        } finally {
          if (this.resetNoteComponent) {
            this.$nextTick(() => {
              this.resetNoteComponent = false;
            });
          }
        }
      }
    },
  },
};
</script>

<style lang="scss">
$medium-font-family: 'DIN Next LT W04 Medium', Roboto, -apple-system,
  BlinkMacSystemFont, 'Segoe UI', Oxygen, Ubuntu, Cantarell, 'Fira Sans',
  'Droid Sans', 'Helvetica Neue', sans-serif;

.change-title {
  font-family: $medium-font-family;
}

.note-text {
  width: 400px;
}

.loading {
  text-align: center;
  position: absolute;
  z-index: 9;
  padding: 8px 18px;
  border-radius: 5px;
  left: calc(50% - 45px);
  top: calc(50% - 18px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.list-group {
  overflow: auto;
  max-height: 50vh;
}
.alert_success {
  display: flex !important;
  margin-top: 10px;
}

.history-header-container {
  display: flex;
  color: #6e6e6e;
}

.history-content-container {
  border-radius: 8px 8px 0 0;
  background-color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1px;
  min-height: 48px;
  padding: 16px;
  width: 100%;
}

.history-upload-state {
  min-height: 48px;
}

.container--note-upload-pending {
  display: flex;
  min-height: 190px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
